import { alert } from 'actions/flash'
import {
  useGetEmailReplyRoutingQuery,
  useUpdateEmailReplyRoutingMutation,
} from 'api/emailReplyRouting'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  AddressError,
  buildStoredValue,
  deriveSectionState,
  ReplyMode,
  SectionState,
  validateSection,
} from './helpers'

const t = buildTranslate('cms.settings.email_reply_routing')

export type SectionController = {
  state: SectionState
  error: AddressError
  onModeChange: (mode: ReplyMode) => void
  onAddressChange: (address: string) => void
  onAddressBlur: () => void
}

type Section = {
  state: SectionState
  setState: Dispatch<SetStateAction<SectionState>>
  setError: Dispatch<SetStateAction<AddressError>>
  controller: SectionController
}

// One channel's radio mode + address field. Callbacks are memoized so the
// controller handed to ReplySection keeps stable references across renders.
const useSection = (): Section => {
  const [state, setState] = useState<SectionState>({
    mode: 'no_reply',
    address: '',
  })
  const [error, setError] = useState<AddressError>(null)

  const onModeChange = useCallback((mode: ReplyMode) => {
    setState(prev => ({ ...prev, mode }))
    setError(null)
  }, [])

  const onAddressChange = useCallback((address: string) => {
    setState(prev => ({ ...prev, address }))
    // Recompute synchronously only while an error is showing, so it clears as
    // the user corrects the value.
    setError(prev =>
      prev ? validateSection({ mode: 'address', address }) : prev
    )
  }, [])

  const onAddressBlur = useCallback(() => {
    setError(validateSection(state))
  }, [state])

  const controller = useMemo<SectionController>(
    () => ({ state, error, onModeChange, onAddressChange, onAddressBlur }),
    [state, error, onModeChange, onAddressChange, onAddressBlur]
  )

  return { state, setState, setError, controller }
}

export type EmailReplyRoutingForm = {
  isError: boolean
  isSaving: boolean
  /** True once the GET + merchant have loaded and the form is seeded. */
  ready: boolean
  marketing: SectionController
  transactional: SectionController
  save: () => Promise<void>
}

/**
 * Form state + persistence for the Email reply addresses page. Reads/writes the
 * `reply_to_configuration` merchant-api resource, maps each channel's stored
 * string to a radio mode + address (see ./helpers), and surfaces per-section
 * controllers the page renders with ReplySection.
 */
const useEmailReplyRoutingForm = (): EmailReplyRoutingForm => {
  const dispatch = useDispatch()
  const merchant = useCurrentMerchant()
  const { data, isLoading, isError } = useGetEmailReplyRoutingQuery()
  const [updateRouting, { isLoading: isSaving }] =
    useUpdateEmailReplyRoutingMutation()

  const marketing = useSection()
  const transactional = useSection()
  const { setState: setMarketing, setError: setMarketingError } = marketing
  const { setState: setTransactional, setError: setTransactionalError } =
    transactional

  // Track which merchant the form was seeded for, so switching the active
  // merchant (which can happen without a remount) re-seeds from its values
  // rather than leaving the previous merchant's addresses in the form.
  const [seededMerchantId, setSeededMerchantId] = useState<number | null>(null)

  useEffect(() => {
    if (!data || !merchant || seededMerchantId === merchant.id) return
    const routing = data.reply_to_configuration
    setMarketing(deriveSectionState(routing.reply_to_marketing))
    setTransactional(deriveSectionState(routing.reply_to_transactional))
    setMarketingError(null)
    setTransactionalError(null)
    setSeededMerchantId(merchant.id)
  }, [
    data,
    merchant,
    seededMerchantId,
    setMarketing,
    setTransactional,
    setMarketingError,
    setTransactionalError,
  ])

  const save = useCallback(async () => {
    const mError = validateSection(marketing.state)
    const tError = validateSection(transactional.state)
    setMarketingError(mError)
    setTransactionalError(tError)
    if (mError || tError) return

    try {
      await updateRouting({
        reply_to_configuration: {
          reply_to_marketing: buildStoredValue(marketing.state),
          reply_to_transactional: buildStoredValue(transactional.state),
        },
      }).unwrap()
      dispatch(
        alert({ key: 'success', message: t('save_success'), timeout: 5 })
      )
    } catch {
      dispatch(alert({ key: 'danger', message: t('save_error'), timeout: 5 }))
    }
  }, [
    marketing.state,
    transactional.state,
    updateRouting,
    dispatch,
    setMarketingError,
    setTransactionalError,
  ])

  return {
    isError,
    isSaving,
    ready: !isLoading && merchant != null && seededMerchantId === merchant.id,
    marketing: marketing.controller,
    transactional: transactional.controller,
    save,
  }
}

export default useEmailReplyRoutingForm
